import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createFormPermissions, toPermissionsRequest } from "@/helpers/permissions";
import { useSlug } from "@/hooks/useSlug";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";

import { DocumentRootForm, type DocumentRootFormProps, type DocumentRootFormValues } from "./DocumentRootForm";

export function CreateDocumentRootPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();

  const documentQueries = useDocumentQueries();
  const permissionQueries = usePermissionQueries();

  const {
    data: roles = [],
    isFetching: rolesIsFetching,
    error: rolesError,
  } = useQuery({
    ...permissionQueries.listRoles(),
    notifyOnChangeProps: ["data", "isFetching", "error"],
  });
  const {
    data: defaultFolderPermissions = [],
    isFetching: defaultFolderPermissionsIsFetching,
    error: defaultFolderPermissionsError,
  } = useQuery({
    ...documentQueries.defaultFolderPermissions(),
    notifyOnChangeProps: ["data", "isFetching", "error"],
  });

  const createRootMutation = documentMutations.useCreateRootFolder();

  const loading = rolesIsFetching || defaultFolderPermissionsIsFetching;
  if (loading) {
    return <FullSizeLoader withPadding />;
  }

  const error = rolesError || defaultFolderPermissionsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const onSubmit = (data: DocumentRootFormValues) => {
    const body = {
      name: data.name,
      description: data.description,
      nameTranslations: [],
      descriptionTranslations: [],
      audience: data.audience,
      iconChar: data.iconChar,
      permissions: toPermissionsRequest(data.permissions),
    };

    return createRootMutation.mutate(body, {
      onSuccess: () => {
        void navigate(routes.documents.rootList({ slug }));
      },
    });
  };

  const defaultValues: DocumentRootFormValues = {
    name: "",
    description: "",
    audience: [],
    permissions: createFormPermissions(roles, defaultFolderPermissions, undefined),
    iconChar: "\uEECA",
  };

  const formProps: DocumentRootFormProps = {
    originalLanguageId: undefined,
    onSubmit,
    defaultValues,
    isSubmitting: createRootMutation.isPending,
    isEditMode: false,
    folderId: undefined,
  };

  return <DocumentRootForm {...formProps} />;
}
